import { useState } from 'react'
import { createRoute, Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetLeadTranslationBreakdown } from '@/application/services/translation.service'
import { useGetAllFactsByLead } from '@/application/services/fact.service'
import type { Fact } from '@/domains/types'
import { Breadcrumb } from '@/ui/components/common/Breadcrumb'
import { Button } from '@/ui/components/common/Button'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { Card, CardContent } from '@/ui/components/common/Card'
import { EditFactDialog } from '@/ui/components/facts/EditFactDialog'
import { translationsRoute } from '@/ui/pages/management/translations'
import {
  TranslationCompletenessTable,
  type TranslationCompletenessRow,
} from '../components/TranslationCompletenessTable'
import { TranslationEntityEditAction } from '../components/TranslationEntityEditAction'

export const translationsLeadDetailRoute = createRoute({
  getParentRoute: () => translationsRoute,
  path: '$investigationId/leads/$leadId',
  component: TranslationLeadDetailPage,
})

export function TranslationLeadDetailPage() {
  const { t } = useTranslation()
  const { investigationId: routeInvestigationId, leadId: routeLeadId } = useParams({
    strict: false,
  })
  const investigationId = routeInvestigationId ?? ''
  const leadId = routeLeadId ?? ''
  const { data: breakdown, isLoading: isLoadingBreakdown } = useGetLeadTranslationBreakdown(leadId)
  // `useGetAllFactsByLead`, not `useGetFactsByLead` — the latter only returns
  // published facts, while completeness is computed over EVERY fact. Using
  // the published-only hook would silently disable the edit action for
  // unpublished facts, exactly the ones most likely to still need
  // translation.
  const { data: facts, isLoading: isLoadingFacts } = useGetAllFactsByLead(leadId)
  const [editingFact, setEditingFact] = useState<Fact | null>(null)

  const isLoading = isLoadingBreakdown || isLoadingFacts

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!breakdown) {
    return (
      <div className="space-y-4">
        <Link to="/manage/translations/$investigationId" params={{ investigationId }}>
          <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            {t('translations.detail_back_to_investigation')}
          </Button>
        </Link>
        <p className="text-muted-foreground">{t('translations.detail_not_found')}</p>
      </div>
    )
  }

  const factsById = new Map((facts ?? []).map((fact) => [fact.id, fact]))

  const rows: TranslationCompletenessRow[] = breakdown.facts.map((factNode) => {
    const fact = factsById.get(factNode.id) ?? null
    if (!fact && import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- dev-only diagnostic, never rendered in production
      console.warn(
        `[translations] fact ${factNode.id} is in the breakdown but not in useGetAllFactsByLead — edit action disabled for this row`,
      )
    }
    return {
      id: factNode.id,
      label: factNode.label,
      locales: factNode.aggregate,
      actions: fact ? (
        <TranslationEntityEditAction
          onEdit={() => setEditingFact(fact)}
          ariaLabel={t('translations.detail_edit_fact_aria', { title: factNode.label })}
        />
      ) : undefined,
    }
  })

  const leadOwnRow: TranslationCompletenessRow = {
    id: breakdown.lead.id,
    label: t('translations.detail_row_lead_own'),
    locales: breakdown.lead.own,
    emphasis: true,
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: t('translations.dashboard_title'), href: '/manage/translations' },
          {
            label: breakdown.investigationName,
            href: `/manage/translations/${investigationId}`,
          },
          { label: breakdown.lead.label },
        ]}
      />

      <h2 className="text-2xl font-bold">{breakdown.lead.label}</h2>

      <Card>
        <CardContent className="space-y-3">
          <TranslationCompletenessTable
            labelHeader={t('translations.detail_col_fact')}
            emptyMessage={t('translations.detail_facts_empty')}
            isLoading={false}
            rows={[leadOwnRow, ...rows]}
          />
          {breakdown.facts.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('translations.detail_facts_empty')}</p>
          )}
        </CardContent>
      </Card>

      <EditFactDialog
        fact={editingFact}
        leadId={leadId}
        open={editingFact !== null}
        onOpenChange={(open) => {
          if (!open) setEditingFact(null)
        }}
      />
    </div>
  )
}
